import React, {
  MutableRefObject,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import HighchartsReact, {
  HighchartsReactRefObject,
} from 'highcharts-react-official';
import Highcharts from 'highcharts';
import Histograms from 'highcharts/modules/histogram-bellcurve';
import ChartEvents from 'highcharts-custom-events';
import Highmaps from 'highcharts/modules/map';
import SolidGauge from 'highcharts/modules/solid-gauge.js';
import HighchartsMore from 'highcharts/highcharts-more.js';
import Drilldown from 'highcharts/modules/drilldown';
import Treemap from 'highcharts/modules/treemap';
import Heatmap from 'highcharts/modules/heatmap';
import Funnel from 'highcharts/modules/funnel';
import Sankey from 'highcharts/modules/sankey';
import Row from './Common/Row';
import DashboardGadgetReportLinkButton from './DashboardGadgetReportLinkButton';
import PopupGridButton from './PopupGridButton';
import DashboardGadgetFilteringButton from './DashboardGadgetFilteringButton';
import RemindersExportButton from './RemindersGadget/RemindersExportButton';
import ShowOnMouseOverUnlessTour from './ShowOnMouseOverUnlessTour';
import CopyGadgetButton from './CopyGadgetButton';
import CopyDashboardGadgetToReportButton from './CopyDashboardGadgetToReportButton';
import NoDataToDisplay from './V5Gadget/NoDataToDisplay';
import { withChartPalette } from './V5Gadget/chartPalette';
import { useChartPalette } from '../contexts/ChartPaletteContext';
import FlexCentered from './Common/FlexCentered';
import CanvasCardTitle from './CanvasCardTitle';
import CardContext from '../contexts/CardContext';
import CanvasCard from './Visualisations/CanvasCard';
import CopyReportGadgetButton from './CopyReportGadgetButton/CopyReportGadgetButton';
import CopyReportGadgetToDashboardButton from './CopyReportGadgetToDashboardButton';
import MetricsInfoButton from './MetricsInfoButton';
import ReportGadgetEditOption from './ReportGadgetEditOption';
import ReportGadgetContext from '../contexts/ReportGadgetContext';
import ChartOptionsTransformContext from '../contexts/ChartOptionsTransformContext';
import { Z_INDEX } from '../constants';
import ContextMenu, { Option } from '../kingpin/atoms/ContextMenu';
import isRemindersGadget from '../types/visTypeCheckers/isRemindersGadget';
import HighchartStyles from './HighchartStyles';
import FleetOpsStaffOnly from './Common/FleetOpsStaffOnly';
import useReportAnIssueModal from './ReportAnIssue/useReportAnIssueModal';
import GadgetFullscreenButton from './GadgetFullscreen/GadgetFullscreenButton';
import { useGadgetFullscreen } from './GadgetFullscreen/GadgetFullscreenProvider';

Histograms(Highcharts);
HighchartsMore(Highcharts);
ChartEvents(Highcharts);
Highmaps(Highcharts);
Drilldown(Highcharts);
Treemap(Highcharts);
Heatmap(Highcharts);
Funnel(Highcharts);
Sankey(Highcharts);
SolidGauge(Highcharts);
// @ts-ignore
Highcharts.SVGRenderer.prototype.symbols['c-rect'] = function (x, y, w, h) {
  const move = ['M', x, y + h / 2];
  const draw = ['L', x + w, y + h / 2];

  return [...move, ...draw];
};

// The buttons on the right of a gadget's header: its report link, metrics
// info and ⋮ menu, revealed on hover. Studio blocks on a dashboard or report
// reuse them in their own header, with their own edit option (Studio's
// settings, which hold their filters too) in place of the classic ones, and
// their dates for the view as a calendar icon beside the menu.
export const GadgetMenuButtons = ({
  chartDefinition,
  extraControl,
  isForcedVisible,
  editOptions,
  datesButton,
  filtersButton,
  fullscreenButton,
  extraMenuItems,
}: {
  chartDefinition: VisualisationDefinition;
  extraControl?: JSX.Element;
  isForcedVisible?: boolean;
  // In place of the classic Edit and Edit Filters options
  editOptions?: JSX.Element;
  // Before the filters button; shows and hides itself on hover
  datesButton?: JSX.Element;
  // Beside the menu, in place of its Edit and Edit Filters options
  filtersButton?: JSX.Element;
  // In place of the classic gadget's (GadgetFullscreenButton); shows and
  // hides itself on hover
  fullscreenButton?: JSX.Element;
  // A Studio block's own options (see BlockMenuItems), after View Raw Data
  extraMenuItems?: JSX.Element;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const onMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
  }, []);
  const onMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);
  const reportGadgetContext = useContext(ReportGadgetContext);
  // Set by the preview surfaces that render a gadget from an unsaved chartDef
  // inside a modal (gadget config, report-gadget config, Insights Explore).
  // There the copy actions below have no dashboard or report to copy from, so
  // they're either dead or a worse duplicate of that surface's own save.
  const { isForConfigureDashboardGadgetPopup } = useContext(CardContext);
  const { isFullscreen } = useGadgetFullscreen();
  const isClassicEditShown = !filtersButton && !editOptions;
  const { open: openReportAnIssue, modal: reportAnIssueModal } =
    useReportAnIssueModal({
      type: reportGadgetContext?.reportGadget
        ? 'report-gadget'
        : 'dashboard-gadget',
      gadgetName: chartDefinition.name,
    });

  return (
    <Row centerAlign>
      {!isFullscreen && (
        <ShowOnMouseOverUnlessTour>
          <DashboardGadgetReportLinkButton />
        </ShowOnMouseOverUnlessTour>
      )}
      <ShowOnMouseOverUnlessTour isForcedVisible={isForcedVisible}>
        <>
          {!!extraControl && (
            <div style={{ marginRight: 8 }}>{extraControl}</div>
          )}
        </>
      </ShowOnMouseOverUnlessTour>
      {fullscreenButton ?? <GadgetFullscreenButton />}
      {!isRemindersGadget(chartDefinition) && !isFullscreen && (
        <ShowOnMouseOverUnlessTour>
          <div style={{ marginRight: 8 }}>
            <MetricsInfoButton visualisation={chartDefinition} />
          </div>
        </ShowOnMouseOverUnlessTour>
      )}
      {datesButton}
      {filtersButton && (
        <ShowOnMouseOverUnlessTour isForcedVisible={isForcedVisible}>
          <div style={{ marginRight: 4 }}>{filtersButton}</div>
        </ShowOnMouseOverUnlessTour>
      )}
      {!isFullscreen && (
        <ShowOnMouseOverUnlessTour
          isForcedVisible={isForcedVisible || isMenuOpen}
        >
          <ContextMenu
            buttonType="Ghost"
            onOpen={onMenuOpen}
            onClose={onMenuClose}
            testId="report-gadget-card-menu"
          >
            <>
              {editOptions}
              {isClassicEditShown && <ReportGadgetEditOption />}
              <PopupGridButton chartDefinition={chartDefinition} />
              {extraMenuItems}
              {isClassicEditShown && <DashboardGadgetFilteringButton />}
              {isRemindersGadget(chartDefinition) && <RemindersExportButton />}
              {!isForConfigureDashboardGadgetPopup &&
                (reportGadgetContext?.reportGadget ? (
                  <>
                    <CopyReportGadgetToDashboardButton
                      reportGadget={reportGadgetContext.reportGadget}
                    />
                    <CopyReportGadgetButton
                      reportGadget={reportGadgetContext.reportGadget}
                    />
                  </>
                ) : (
                  <>
                    <CopyGadgetButton label="Copy to Dashboard" />
                    <CopyDashboardGadgetToReportButton
                      chartDef={chartDefinition}
                    />
                  </>
                ))}
              <FleetOpsStaffOnly>
                <Option
                  label="Report an issue"
                  onClick={openReportAnIssue}
                  testId="report-an-issue"
                />
              </FleetOpsStaffOnly>
            </>
          </ContextMenu>
        </ShowOnMouseOverUnlessTour>
      )}
      {reportAnIssueModal}
    </Row>
  );
};

export const ChartTopBar = ({
  chartDefinition,
  extraControl,
  isForcedVisible,
}: {
  chartDefinition?: VisualisationDefinition;
  extraControl?: JSX.Element;
  isForcedVisible?: boolean;
}) => {
  if (!chartDefinition) {
    return null;
  }

  return (
    <Row
      spaceBetween
      style={{
        zIndex: 'unset',
        width: '100%',
        height: '100%',
      }}
      centerAlign
    >
      <CanvasCardTitle title={chartDefinition.name} />
      <GadgetMenuButtons
        chartDefinition={chartDefinition}
        extraControl={extraControl}
        isForcedVisible={isForcedVisible}
      />
    </Row>
  );
};

export const ChartContent = ({
  chartRef,
  options,
  zIndex,
  noDataToDisplay,
  useSmallNoDataMessage,
}: {
  chartRef: MutableRefObject<HighchartsReactRefObject | null>;
  options: any;
  zIndex?: number;

  noDataToDisplay?: boolean;
  useSmallNoDataMessage?: boolean;
}) => {
  const transform = useContext(ChartOptionsTransformContext);
  const palette = useChartPalette();
  // The chart's own palette first, so a transform (e.g. a forecast) sees
  // the colours it's drawn with
  const finalOptions = useMemo(() => {
    const coloured =
      palette.isCustom && options
        ? withChartPalette({ options, colors: palette.colors })
        : options;
    return transform && coloured ? transform(coloured) : coloured;
  }, [options, palette, transform]);

  if (noDataToDisplay) {
    return (
      <NoDataToDisplay size={useSmallNoDataMessage ? 'small' : 'normal'} />
    );
  } else {
    return (
      <HighchartStyles style={{ height: '100%', width: '100%' }}>
        <FlexCentered
          style={{
            height: '100%',
            width: '100%',
            padding: '0px 8px',
          }}
        >
          <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            options={finalOptions}
            containerProps={{
              style: {
                width: '100%',
                height: '100%',
                zIndex: zIndex === undefined ? Z_INDEX.CHART : zIndex,
              },
            }}
          />
        </FlexCentered>
      </HighchartStyles>
    );
  }
};

const Chart = ({
  chartRef,
  options,
  zIndex,
  chartDefinition,
  bottomBar,
  noDataToDisplay,
  useSmallNoDataMessage,
  isGauge,
}: {
  chartRef: MutableRefObject<any>;
  options: any;
  zIndex?: number;
  chartDefinition?:
    | VisualisationDefinitions.Gauge
    | VisualisationDefinitions.SingleMetricDateMatrix
    | V5ChartDefinition;
  bottomBar?: JSX.Element | JSX.Element[];
  noDataToDisplay?: boolean;
  useSmallNoDataMessage?: boolean;
  isGauge?: boolean;
}) => (
  <CanvasCard
    topBar={
      chartDefinition ? (
        <ChartTopBar chartDefinition={chartDefinition} />
      ) : undefined
    }
    content={
      <ChartContent
        chartRef={chartRef}
        options={options}
        zIndex={zIndex}
        noDataToDisplay={noDataToDisplay}
        useSmallNoDataMessage={useSmallNoDataMessage}
      />
    }
    bottomBar={bottomBar}
    isBottomBarAbsolute={isGauge}
  />
);

export default Chart;
